// ─── Offline Mutation Queue ────────────────────────────────────────────────────
// Queues failed mutations (network errors only) in localStorage and replays
// them when the browser comes back online. Designed for habit toggles and
// check-in submits — the two most time-sensitive client actions.
//
// Usage:
//   import { offlineQueue } from '@/lib/offlineQueue';
//   try { await mutation.mutateAsync(payload); }
//   catch (e) { offlineQueue.enqueue('checkins.submit', payload); }
//
// The queue auto-replays on `online` events. Callers don't need to poll.

const STORAGE_KEY = 'the_standard_offline_queue';

export type QueuedMutation = {
  id: string;
  action: string;       // tRPC procedure path, e.g. 'checkins.submit'
  payload: unknown;
  queuedAt: number;     // Date.now()
  retries: number;
};

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function readQueue(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedMutation[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // localStorage full — silently drop oldest
    try {
      const trimmed = queue.slice(-10);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch { /* give up */ }
  }
}

/** Add a failed mutation to the offline queue. */
function enqueue(action: string, payload: unknown): void {
  const queue = readQueue();
  queue.push({
    id: generateId(),
    action,
    payload,
    queuedAt: Date.now(),
    retries: 0,
  });
  writeQueue(queue);
}

/** Remove a specific item by ID. */
function dequeue(id: string): void {
  const queue = readQueue().filter(item => item.id !== id);
  writeQueue(queue);
}

/** Get pending count. */
function pendingCount(): number {
  return readQueue().length;
}

/** Clear the entire queue. */
function clear(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/** Replay all queued mutations. Called on `online` event.
 *  Takes a replay function that receives (action, payload) and returns
 *  a promise. Items that succeed are removed; items that fail again
 *  stay in the queue (up to 5 retries, then dropped).
 */
async function replay(
  executor: (action: string, payload: unknown) => Promise<void>,
): Promise<{ replayed: number; failed: number }> {
  const queue = readQueue();
  if (queue.length === 0) return { replayed: 0, failed: 0 };

  let replayed = 0;
  let failed = 0;
  const remaining: QueuedMutation[] = [];

  for (const item of queue) {
    try {
      await executor(item.action, item.payload);
      replayed++;
    } catch {
      item.retries++;
      if (item.retries < 5) {
        remaining.push(item);
      }
      failed++;
    }
  }

  writeQueue(remaining);
  return { replayed, failed };
}

export const offlineQueue = {
  enqueue,
  dequeue,
  pendingCount,
  clear,
  replay,
  readQueue,
};
